import { ISelectOption } from './select.interfaces';
import {
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import type {
    TProtocolControlType,
    TProtocolItemsGroup,
} from './protocol.types';
export type {
    TProtocolControlType,
    TProtocolItemsGroup,
} from './protocol.types';

/**
 * Способ ввода данных в протокол (приходит с базы)
 * */
export enum ProtocolItemInputType {
    string, //0  - Простой текст (строка),
    textarea, //1  - Описание (много строк),
    select_drop_down, //2  - Значения из списка,
    ref_select, //3  - Значения из внешнего справочника,
    formula, //4  - Формула,
    table, //5  - Таблица,
    sys_ref, //6  - Значение из системного справочника,
    sql, //7  - Формула SQL,
    diagnos_field, //8  - Поле для ввода диагноза,
    services_field, //9  - Поле для ввода услуг,
    tree, //10 - Значение из дерева,
    select_buttons, //11 - Значение из списка отмеченное галочками,
    file, //12 - Файл,
    schema, //13 - Схема (изображение);
}

/**
 * Формат хранения value в базе
 * */
export enum ProtocolItemDataType {
    string, //0-Текст,
    date, //1-Дата,
    time, //2-Время,
    datetime, //3-Дата и время,
    bool, //4-Логический,
    float, //5-Число с плавающей точкой,
    id, //6-Идентификатор,
    number, //7-Целое число,
    text, //8-Большой текст (clob)
}

/**
 * Разделитель, не разделитель
 */
export enum ProtocolItemType {
    delimeter, //0 - Разделитель
    editable, //1 - Рeдактируемый
}

export interface IProtocolInfo {
    isTruePat: number;
    isPaid: number;
    visitDat: string;
    visitTime: string;
    isExists: number;
}

export interface IResponseCreateProtocol {
    identity: number;
}

/**
 * Приходящий с базы
 */
export interface IProtocolItem {
    formId: number;
    formItemId: number;
    formName: string;
    typ: number;
    valueType: number;
    dataType: number;
    isMulti: number;
    isRequired: number;
    questionName: string;
    formResultId: number;
    formResultValueId?: number;
    formResultValueText?: string | null;
    formItemValId?: number;
    formItemValText?: string;
    formValueId: number | null;
    formItemVals?: IQuestionsItems[];
    formResultVals?: IQuestionsItems[];
}
interface IQuestionsItems {
    id: number;
    text: string;
}

/**
 * Тип поддерживаемых инпутов в протоколе
 */
/**
 * Конструктор протокола. Принимает записи с базы, возврадает на их основании новый протокол.
 */
export const ProtocolBuilder = (items: IProtocolItem[]) => {
    const formItemIdMap = new Map<number, IProtocolItem[]>();
    items.reduce((map, item) => {
        const formItem = map.get(item.formItemId);

        if (formItem) formItem.push(item);
        else map.set(item.formItemId, [item]);

        return map;
    }, formItemIdMap);
    // console.log(formItemIdMap);

    const protocolItemsGroup = Array.from(formItemIdMap);

    const controls = protocolItemsGroup.map(
        (item: TProtocolItemsGroup) => new ProtocolControl(item),
    );

    return new Protocol(controls);
};

/**
 * Основной класс для работы с протоколами
 * Создаётся при помощи ProtocolBuilder
 */
export class Protocol {
    public controls: ProtocolControl[];
    public form: FormGroup = new FormGroup({});

    public constructor(controls: ProtocolControl[]) {
        this.controls = controls;
        this.initForm(controls);
    }

    /**
     * Инициализация формы
     */
    private initForm(controls: ProtocolControl[]) {
        controls.forEach((control) => {
            const validators: ValidatorFn[] = [];

            if (control.isRequired) validators.push(Validators.required);

            this.addFormControl(control, validators);
        });
    }

    /**
     * Добавляет новый FormControl в поле form. Можно передать массив валидаций
     * @param control  Поле протокола
     * @param validators Валидаторы поля
     */
    public addFormControl(
        control: ProtocolControl,
        validators: ValidatorFn[] = [],
    ): void {
        this.form.addControl(
            control.name,
            new FormControl(control.defaultValue, [...validators]),
        );
        this.form.get('path');
    }

    /**
     * Получение массива записей с параметрами для сохранения протокола в БД
     */
    public prepareToSave(visitID: number) {
        return this.controls.map((control) => {
            let valuesText = ''; // Значение / значения ('груша;яблоко')
            let valuesId = ''; // id / ids значений  ('1;3')
            let valueText = ''; // Отображаемое значение  (Груша, яблоко)

            const formControl = this.form.get(control.id.toString());

            if (control.type === 'multi-select') {
                valuesText =
                    control
                        .item!.formResultVals?.map((item) => item.text)
                        .join(';') ?? '';
                valuesId =
                    control
                        .item!.formResultVals?.map((item) => item.id)
                        .join(';') ?? '';
                valueText = valuesText.replace(';', ', ');
            } else if (control.type === 'select') {
                valuesText = control.item.formResultVals?.[0]
                    ? control.item.formResultVals?.[0].text
                    : '';
                valuesId = control.item.formResultVals?.[0]
                    ? control.item.formResultVals?.[0].id.toString()
                    : '';
                valueText = control.item.formResultVals?.[0]
                    ? control.item.formResultVals?.[0].text
                    : '';
            } else if (control.type === 'text') {
                valueText = formControl?.value;
            } else if (control.type === 'date') {
                valueText = formControl?.value;
            }

            if (valueText === null) valueText = '';
            if (valuesId === null) valuesId = '';
            if (valuesText === null) valuesText = '';

            return {
                p_visit_id: +visitID,
                p_form_result_id: +control.item.formResultId,
                p_form_item_id: +control.item.formItemId,
                p_values_text: valuesText,
                p_values_id: valuesId,
                p_text: valueText,
            };
        });
    }
}

/**
 * Поле/контрол протокола
 */
export class ProtocolControl {
    public base: TProtocolItemsGroup; // Содержит все записи из базы по протоколу с одинаковым form_item_id
    public id: number;
    public name: string;
    public label?: string;
    public isRequired: boolean;
    public options?: ISelectOption[]; // Опшены для списков
    public isMultiSelect: boolean;
    public item: IProtocolItem;
    public defaultValue: any = '';
    public dataType: number;

    public constructor(base: TProtocolItemsGroup) {
        this.base = base;
        const [id, items] = base;
        const item = items[0];

        // Присвоение общей информации об инпуте
        this.item = item;
        this.id = id;
        this.name = this.id.toString();
        this.label = item.questionName ?? undefined;
        this.isRequired = !!item.isRequired;
        this.isMultiSelect = !!item.isMulti;
        this.dataType = item.dataType;

        // Присвоение дефолтного value
        if (
            this.item.valueType === ProtocolItemInputType.select_buttons ||
            this.item.valueType === ProtocolItemInputType.select_drop_down ||
            this.item.valueType === ProtocolItemInputType.ref_select
        ) {
            this.options = this.initOptions(item);
            this.defaultValue = this.isMultiSelect
                ? this.options?.filter((opt) => opt.selected)
                : this.options?.find((opt) => opt.selected);
        } else if (this.item.valueType === ProtocolItemInputType.string) {
            this.defaultValue = this.item.formResultValueText;
        }
    }

    /**
     * Формирует массив опшенов на основании записей из базы
     */
    public initOptions(itemObject: IProtocolItem) {
        return itemObject.formItemVals?.map((item) => ({
            value: item.id,
            text: item.text,
            selected:
                itemObject.formResultVals?.some((val) => val.id === item.id) ||
                false,
        }));
    }

    /**
     * Получение типа контрола
     */
    public get type(): TProtocolControlType {
        const { typ, dataType, valueType } = this.item;

        if (typ === ProtocolItemType.delimeter) return 'delimiter';
        if (dataType === ProtocolItemDataType.date) return 'date';

        if (
            valueType === ProtocolItemInputType.select_drop_down ||
            valueType === ProtocolItemInputType.select_buttons ||
            (valueType === ProtocolItemInputType.ref_select &&
                this.options &&
                this.options.length > 0)
        ) {
            return this.isMultiSelect ? 'multi-select' : 'select';
        }

        if (
            valueType === ProtocolItemInputType.ref_select &&
            this.options &&
            this.options.length === 0
        ) {
            return 'text';
        }

        return 'text';
    }
}
